import { useState, useEffect, useCallback, Component, type ReactNode } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useTvData } from './hooks/useTvData';
import TvLeaderboardSlide from './slides/TvLeaderboardSlide';
import TvScheduleSlide from './slides/TvScheduleSlide';
import TvSponsorsSlide from './slides/TvSponsorsSlide';
import TvGallerySlide from './slides/TvMultimediaSlide';
import TvPairingsSlide from './slides/TvPairingsSlide';
import TvStatusBar from './components/TvStatusBar';

class TvErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean; error: string }> {
  state = { hasError: false, error: '' };
  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error: error.message };
  }
  componentDidCatch(error: Error) {
    console.error('TV Display error:', error);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="fixed inset-0 bg-golf-gray flex flex-col items-center justify-center text-white">
          <div className="w-24 h-24 rounded-full bg-red-500/20 flex items-center justify-center mb-6">
            <span className="text-5xl">⚠</span>
          </div>
          <h2 className="text-3xl font-bold mb-3">Display Error</h2>
          <p className="text-white/40 text-lg mb-4">{this.state.error}</p>
          <button
            onClick={() => { this.setState({ hasError: false, error: '' }); window.location.reload(); }}
            className="px-6 py-3 bg-golf-orange rounded-lg text-white font-bold"
          >
            Reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

const SLIDE_DURATION = 20000;

interface SlideConfig {
  id: string;
  label: string;
  icon: string;
  show: (data: ReturnType<typeof useTvData>) => boolean;
}

const SLIDES: SlideConfig[] = [
  {
    id: 'leaderboard',
    label: 'Leaderboard',
    icon: '🏆',
    show: ({ leaderboard }) => leaderboard.length > 0,
  },
  {
    id: 'pairings',
    label: 'Pairings',
    icon: '👥',
    show: ({ rounds }) => rounds.length > 0,
  },
  {
    id: 'schedule',
    label: 'Schedule',
    icon: '📅',
    show: ({ events }) => events.length > 0,
  },
  {
    id: 'multimedia',
    label: 'Multimedia',
    icon: '📸',
    show: ({ photos }) => photos.length > 0,
  },
  {
    id: 'sponsors',
    label: 'Sponsors',
    icon: '⭐',
    show: ({ partners }) => partners.length > 0,
  },
];

function LoadingScreen() {
  return (
    <div className="flex flex-col items-center justify-center h-full bg-golf-gray text-white">
      <div className="w-20 h-20 rounded-full border-4 border-golf-orange/30 border-t-golf-orange animate-spin mb-6" />
      <p className="text-white/50 text-xl font-medium">Loading tournament data...</p>
    </div>
  );
}

function NoDataScreen() {
  return (
    <div className="flex flex-col items-center justify-center h-full bg-golf-gray text-white">
      <div className="w-24 h-24 rounded-full bg-golf-green/20 flex items-center justify-center mb-6">
        <span className="text-5xl">⛳</span>
      </div>
      <h2 className="text-4xl font-bold font-display mb-3">Ibom Open Golf Tournament</h2>
      <p className="text-white/40 text-xl">No active tournament data available</p>
    </div>
  );
}

export default function TvDisplay() {
  return (
    <TvErrorBoundary>
      <TvDisplayInner />
    </TvErrorBoundary>
  );
}

function TvDisplayInner() {
  const [selectedTournamentId, setSelectedTournamentId] = useState<string | null>(null);
  const data = useTvData(selectedTournamentId);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [paused, setPaused] = useState(true);

  useEffect(() => {
    if (!selectedTournamentId && data.tournament) {
      setSelectedTournamentId(data.tournament.id);
    }
  }, [data.tournament, selectedTournamentId]);

  const activeSlides = SLIDES.filter((s) => s.show(data));

  const advance = useCallback(() => {
    setCurrentSlide((c) => (c + 1) % Math.max(1, activeSlides.length));
  }, [activeSlides.length]);

  useEffect(() => {
    if (paused || activeSlides.length <= 1) return;
    const timer = setInterval(advance, SLIDE_DURATION);
    return () => clearInterval(timer);
  }, [advance, paused, activeSlides.length]);

  useEffect(() => {
    if (currentSlide >= activeSlides.length && activeSlides.length > 0) {
      setCurrentSlide(0);
    }
  }, [activeSlides.length, currentSlide]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') advance();
      if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        setCurrentSlide((c) => (c - 1 + activeSlides.length) % Math.max(1, activeSlides.length));
      }
      if (e.key === ' ') setPaused((p) => !p);
    },
    [advance, activeSlides.length]
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const slide = activeSlides[currentSlide];

  const renderSlide = () => {
    if (!slide) return null;
    if (slide.id === 'leaderboard') {
      return (
        <TvLeaderboardSlide
          tournament={data.tournament}
          leaderboard={data.leaderboard}
          partners={data.partners}
          orgLogoUrl={data.orgLogoUrl}
          totalRounds={data.rounds.length}
          roundNames={Object.fromEntries(data.rounds.map((r) => [r.round_number, r.name]))}
          specialPrizes={data.specialPrizes}
          holeParMap={data.holeParMap}
          holeHcpMap={data.holeHcpMap}
        />
      );
    }
    if (slide.id === 'pairings') {
      return (
        <TvPairingsSlide
          tournament={data.tournament}
          rounds={data.rounds}
          orgLogoUrl={data.orgLogoUrl}
        />
      );
    }
    if (slide.id === 'schedule') {
      return <TvScheduleSlide tournament={data.tournament} events={data.events} />;
    }
    if (slide.id === 'multimedia') {
      return <TvGallerySlide tournament={data.tournament} photos={data.photos} />;
    }
    if (slide.id === 'sponsors') {
      return <TvSponsorsSlide tournament={data.tournament} partners={data.partners} />;
    }
    return null;
  };

  return (
    <div
      className="fixed inset-0 bg-golf-gray flex flex-col overflow-hidden"
    >
      {data.loading ? (
        <LoadingScreen />
      ) : !data.tournament ? (
        <NoDataScreen />
      ) : activeSlides.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-full text-white">
          <div className="w-24 h-24 rounded-full bg-golf-green/20 flex items-center justify-center mb-6">
            <span className="text-5xl">⛳</span>
          </div>
          <h2 className="text-3xl font-bold font-display mb-3">{data.tournament.name}</h2>
          <p className="text-white/40 text-lg">Waiting for tournament data...</p>
        </div>
      ) : (
        <>
          <div className="flex-1 relative overflow-hidden">
            <AnimatePresence mode="wait">
              <motion.div
                key={slide?.id ?? 'empty'}
                className="absolute inset-0"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.8, ease: 'easeInOut' }}
              >
                {renderSlide()}
              </motion.div>
            </AnimatePresence>

            <div className="absolute bottom-2 left-0 right-0 flex items-center justify-center gap-1.5 pb-1">
              {activeSlides.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentSlide(i)}
                  className={`rounded-full transition-all duration-300 ${
                    i === currentSlide
                      ? 'w-8 h-2 bg-golf-orange shadow-sm shadow-golf-orange/50'
                      : 'w-2 h-2 bg-white/20 hover:bg-white/40'
                  }`}
                />
              ))}
            </div>
          </div>

          <TvStatusBar
            slides={activeSlides.map((s) => ({ id: s.id, label: s.label, icon: s.icon }))}
            currentSlide={currentSlide}
            lastUpdated={data.lastUpdated}
            tournamentName={data.tournament.name}
            tournaments={data.allTournaments.map((t) => ({ id: t.id, name: t.name }))}
            selectedTournamentId={selectedTournamentId ?? data.tournament.id}
            onTournamentSelect={(id) => { setSelectedTournamentId(id); setCurrentSlide(0); }}
            paused={paused}
            onSlideSelect={setCurrentSlide}
            onPauseToggle={() => setPaused((p) => !p)}
          />
        </>
      )}
    </div>
  );
}
